import React, { useMemo, useRef, useState } from "react";
import {
  Layout,
  Typography,
  Rate,
  Input,
  Upload,
  Button,
  Card,
  Space,
  Divider,
  message,
  Spin,
} from "antd";
import {
  CloudUploadOutlined,
  CloseCircleFilled,
  SendOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import AxiosInstance from "../../services/axiosInstance";

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

export default function Feedback() {
  const { t } = useTranslation();

  const userId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("USER_INFO")) || {};
    } catch {
      return {};
    }
  }, []);

  // MB
  const feedbackSize = Number(userId?.configurationEvvNew?.feedbackSize ?? 150);

  // simple colors (replace with your theme)
  const theme = { dark: false };
  const colors = useMemo(
    () => ({
      background: theme.dark ? "#0f0f10" : "#ffffff",
      title: theme.dark ? "#f5f5f5" : "#111",
      card: theme.dark ? "#2a2a2d" : "#fafafa",
    }),
    [theme.dark]
  );

  const [rating, setRating] = useState(5);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");

  // media state (web)
  const [media, setMedia] = useState(null); // { type: 'image'|'video', previewUrl, name }
  const [finalMedia, setFinalMedia] = useState(null); // { fileUrl }
  const [loading, setLoading] = useState(false);

  const previewRef = useRef(null);

  const accept = "image/*,video/*";

  const resetMedia = () => {
    if (media?.previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(media.previewUrl);
    }
    setMedia(null);
    setFinalMedia(null);
  };

  const uploadToS3 = async (fileObj) => {
    try {
      setLoading(true);

      const fileName = fileObj.name;
      const fileType = fileObj.type;

      // 1) Get presigned URL
      const url = `upload/get-url?fileName=${encodeURIComponent(
        fileName
      )}&fileType=${encodeURIComponent(fileType)}`;

      const res = await AxiosInstance.get(url);

      if (!res.data?.success) {
        throw new Error(res.data?.message || "Failed to get S3 URL");
      }

      const uploadURL = res.data.url.uploadURL;

      // 2) Upload directly to S3 (PUT)
      const uploadRes = await fetch(uploadURL, {
        method: "PUT",
        headers: { "Content-Type": fileType },
        body: fileObj,
      });

      if (!uploadRes.ok) {
        throw new Error(`Upload failed with status ${uploadRes.status}`);
      }

      const fileUrl = uploadURL.split("?")[0];
      setFinalMedia({ fileUrl });

      message.success(
        t(
          "File uploaded successfully. Please tap the feedback button to complete submission."
        )
      );
    } catch (err) {
      message.error(err?.message || t("Upload failed"));
      resetMedia();
    } finally {
      setLoading(false);
    }
  };

  const beforeUpload = async (file) => {
    const isAllowed =
      file.type.startsWith("image/") || file.type.startsWith("video/");
    if (!isAllowed) {
      message.error(t("Please upload an image or a video."));
      return Upload.LIST_IGNORE;
    }

    const sizeMb = file.size / 1024 / 1024;
    if (sizeMb > feedbackSize) {
      message.error(`${t("File must be smaller than")} ${feedbackSize}MB`);
      return Upload.LIST_IGNORE;
    }

    // preview
    const previewUrl = URL.createObjectURL(file);
    const type = file.type.startsWith("video/") ? "video" : "image";
    setMedia({ type, previewUrl, name: file.name });

    // ✅ scroll to preview after DOM updates (prevents button half-visible)
    requestAnimationFrame(() => {
      previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });

    // upload
    await uploadToS3(file);

    // stop AntD auto upload
    return false;
  };

  const handleFeedbackSubmit = async () => {
    if (!subject.trim()) {
      message.error(t("Please enter a subject before submitting your feedback"));
      return;
    }

    try {
      setLoading(true);

      const feedbackData = {
        subject: subject.trim(),
        userId: userId?._id,
        rating,
        description: description?.trim() || "",
        filePath: finalMedia?.fileUrl || "",
        appVersion: "Web 1.0.0",
        deviceInfo: {
          modelName: "Web",
          osName: "Web",
          osVersion: "Web",
          manufacturer: "Web",
        },
      };

      const res = await AxiosInstance.post("emp/feedback-forms", feedbackData);

      if (res.data?.success) {
        message.success(res.data.message || t("Success"));
        setRating(5);
        setSubject("");
        setDescription("");
        resetMedia();

        // optional: go back to top after submit
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        message.error(res.data?.message || t("Failed"));
      }
    } catch (e) {
      message.error(e?.message || t("Something went wrong. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout style={{ background: colors.background, minHeight: "100vh" }}>
      {/* extra bottom padding so submit never feels cut off */}
      <Content style={{ padding: 20, paddingBottom: 80 }}>
        <Spin spinning={loading}>
          <div style={{ maxWidth: 720 }}>
            {/* Title */}
            <Title level={3} style={{ marginTop: 0, color: colors.title }}>
              {t("Your Feedback Matters")}
            </Title>

            <Text style={{ color: colors.title }}>
              {t("We'd love to hear how your experience has been so far")}!
            </Text>

            <Divider style={{ margin: "16px 0" }} />

            {/* Rating */}
            <Space direction="vertical" size={6} style={{ width: "100%" }}>
              <Rate value={rating} onChange={setRating} style={{ fontSize: 32 }} />
              <Text type="secondary" style={{ fontSize: 13 }}>
                {t("Please click on stars to select your desired rating")}
              </Text>
            </Space>

            <Divider style={{ margin: "16px 0" }} />

            {/* Subject */}
            <Space direction="vertical" size={6} style={{ width: "100%" }}>
            <Text style={{ fontWeight: 600, color: colors.title }}>
                {t("Subject")} <span style={{ color: "red" }}>*</span>
              </Text>

              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t("Enter subject here")}
              />
            </Space>

            <div style={{ height: 14 }} />

            {/* Comment */}
            <Space direction="vertical" size={6} style={{ width: "100%" }}>
              <Text style={{ fontWeight: 600, color: colors.title }}>
                {t("Comment")}
              </Text>
              <TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("Enter your feedback here")}
                rows={6}
              />
            </Space>

            <div style={{ height: 18 }} />

            {/* Media Upload */}
            <Card
              styles={{
                body: { background: theme.dark ? "#171719" : "#fff" },
              }}
              style={{
                borderRadius: 12,
                background: theme.dark ? "#171719" : "#fff",
              }}
            >
              <Text
                style={{
                  display: "block",
                  fontWeight: 600,
                  color: colors.title,
                  textAlign: "center",
                  marginBottom: 12,
                }}
              >
                {t("Attach Image or Video with size limits")} {feedbackSize}MB (
                {t("optional")})
              </Text>

              {media ? (
                <div ref={previewRef} style={{ position: "relative" }}>
                  {media.type === "video" ? (
                    <video
                      src={media.previewUrl}
                      controls
                      style={{
                        width: "100%",
                        height: "auto",
                        maxHeight: 400,
                        borderRadius: 8,
                        objectFit: "cover",
                        background: "#000",
                      }}
                    />
                  ) : (
                    <img
                      src={media.previewUrl}
                      alt="preview"
                      style={{
                        width: "100%",
                        height: "auto",
                        maxHeight: 400,
                        borderRadius: 8,
                        objectFit: "cover",
                      }}
                    />
                  )}

                  <Button
                    type="text"
                    icon={
                      <CloseCircleFilled
                        style={{ color: "#ff4d4f", fontSize: 22 }}
                      />
                    }
                    onClick={resetMedia}
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      background: colors.background,
                      borderRadius: 999,
                    }}
                  />
                </div>
              ) : (
                <div style={{ width: "100%", textAlign: "center", marginBottom: 16 }}>
                  <Upload
                    accept={accept}
                    maxCount={1}
                    beforeUpload={beforeUpload}
                    showUploadList={false}
                    style={{ width: "100%" }}
                  >
                    <Button
                      block
                      size="large"
                      icon={<CloudUploadOutlined />}
                      style={{
                        width: "100%",
                        borderRadius: 10,
                        background: colors.card,
                        borderColor: "#d9d9d9",
                        fontWeight: 600,
                      }}
                    >
                      {t("Upload Image/Video")}
                    </Button>
                  </Upload>
                </div>
              )}
            </Card>

            {/* Submit */}
            <div style={{ marginTop: 16 }}>
              <Button
                type="primary"
                icon={<SendOutlined />}
                size="large"
                block
                loading={loading}
                disabled={loading}
                onClick={handleFeedbackSubmit}
                styles={{ icon: { marginInlineEnd: 0 } }}
                style={{
                  height: 52,
                  borderRadius: 999,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                }}
              >
                {t("Send Feedback")}
              </Button>
            </div>
          </div>
        </Spin>
      </Content>
    </Layout>
  );
}
